import {displayLatestGfr, patientContact} from '@/modules/patients/functions'
import {getFullName} from '@/modules/users/functions'
import {usePatientContext} from '@/shared/contexts/patient-context'
import {Tabs} from '@chakra-ui/react'
import {DataList} from '@cypro/ui/components/ui/data-list'
import {DataSection} from '@cypro/ui/components/ui/data-section'
import {getAge, getShortDateFormatter, isDefined} from '@cypro/ui/utils'
import {useTranslate} from '@tolgee/react'
import {useMemo} from 'react'

export function AboutTab() {
  const {t} = useTranslate('common')
  const {patient} = usePatientContext()

  const aboutItems = useMemo(() => {
    const homeContact = patientContact(patient, 'home')

    return [
      {label: t('cypro_id'), value: patient.identifiers?.[0]?.value},
      {
        label: t('birth_date'),
        value: isDefined(patient.birthDate)
          ? getShortDateFormatter().format(patient.birthDate) + ` (${getAge(patient.birthDate)} ${t('yrs')})`
          : '-',
      },
      {label: t('email'), value: homeContact?.email || '-'},
      {label: t('phone'), value: homeContact?.phone || '-'},
      {
        label: t('address'),
        value: [
          homeContact?.address?.addressLine || '-',
          homeContact?.address?.city || '-',
          homeContact?.address?.region || '-',
          homeContact?.address?.postalCode || '-',
        ].join(', '),
      },
      {label: t('country'), value: homeContact?.address?.country || '-'},
      {label: t('nationality'), value: patient?.nationality || '-'},
    ]
  }, [t, patient])

  const wardPhysicianItems = useMemo(() => {
    const encounter = patient.encounters?.at(-1)

    return [
      {label: t('hospital'), value: patient?.responsibleDoctor?.userAccessLocations?.[0]?.location?.name || '-'},
      {label: t('responsible_doctor'), value: getFullName(patient?.responsibleDoctor, 'last-first') || '-'},
      {label: t('section'), value: encounter?.section?.name || '-'},
      {label: t('ward'), value: encounter?.ward?.name || '-'},
    ]
  }, [t, patient])

  const latest = patient.characteristics?.[0]

  const items = useMemo(
    () => [
      {
        label: t('characteristics.diagnosis'),
        value: latest?.diagnosis?.name || `(${t('no_diagnosis_assigned')})`,
      },
      {
        label: t('sex'),
        value: patient.sex ? t(patient.sex) : '-',
      },
      {
        label: t('characteristics.height'),
        value: latest?.height || '-',
      },
      {
        label: t('characteristics.weight'),
        value: latest?.weight || '-',
      },
      {
        label: t('characteristics.gfr'),
        value: displayLatestGfr(patient),
      },
      {
        label: t('characteristics.bsa'),
        value: latest?.bsa || '-',
      },
      {
        label: t('characteristics.aibw'),
        value: latest?.aibw || '-',
      },
      {
        label: t('characteristics.useAibw'),
        value: latest?.useAibw ? t('yes') : t('no'),
      },
      {
        label: t('characteristics.diabetes'),
        value: latest?.diabetes ? t(`characteristics.diabetes.${latest.diabetes}`) : '-',
      },
      {
        label: t('characteristics.smoker'),
        value: latest?.smoker ? t('yes') : t('no'),
      },
      {
        label: t('characteristics.alcoholic'),
        value: latest?.alcoholic ? t('yes') : t('no'),
      },
    ],
    [t, latest, patient]
  )

  return (
    <Tabs.Content value="about" spaceY="8" display="flex" flexDirection="column">
      <DataSection label={t('contact_information')}>
        <DataList items={aboutItems} />
      </DataSection>
      <DataSection label={t('characteristics')}>
        <DataList items={items} />
      </DataSection>
      <DataSection label={t('ward_physician')}>
        <DataList items={wardPhysicianItems} />
      </DataSection>
    </Tabs.Content>
  )
}
